import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { api } from '@/lib/api';
import {
  ApErrorParams,
  SwaggerAudience,
  SwaggerDefinition,
} from '@activepieces/shared';

import { SwaggerAudienceField } from './swagger-audience-field';

type ImportSource = 'url' | 'paste';

/**
 * Import happens in two steps on purpose: this dialog only creates a draft, and
 * the detail page is where the routes, icon and assignment are reviewed before
 * anything becomes visible to a client. A one-shot "import and publish" would
 * expose whatever the document happens to contain.
 */
const ImportSwaggerDialog = () => {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<ImportSource>('url');
  const [sourceUrl, setSourceUrl] = useState('');
  const [rawSpec, setRawSpec] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [audience, setAudience] = useState<SwaggerAudience>(
    SwaggerAudience.CLIENTS,
  );
  const [tenantIds, setTenantIds] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const reset = () => {
    setSource('url');
    setSourceUrl('');
    setRawSpec('');
    setDisplayName('');
    setBaseUrl('');
    setAudience(SwaggerAudience.CLIENTS);
    setTenantIds([]);
    setErrorMessage(null);
  };

  const { mutate: importSwagger, isPending } = useMutation<
    SwaggerDefinition,
    unknown
  >({
    mutationFn: () =>
      adminApi.createSwagger({
        audience,
        tenantIds,
        ...(displayName.trim().length > 0
          ? { displayName: displayName.trim() }
          : {}),
        ...(baseUrl.trim().length > 0 ? { baseUrl: baseUrl.trim() } : {}),
        ...(source === 'url'
          ? { sourceUrl: sourceUrl.trim() }
          : { spec: rawSpec }),
      }),
    onSuccess: (definition) => {
      setOpen(false);
      reset();
      queryClient.invalidateQueries({ queryKey: ['admin-swaggers'] });
      toast({
        variant: 'success',
        title: t('Swagger imported'),
        description: t(
          'Review the routes and the assignment, then enable it for the client.',
        ),
      });
      navigate(`/internal-admin/swaggers/${definition.id}`);
    },
    onError: (error) => {
      // The server explains precisely why a document was rejected — bad URL,
      // unsupported version, address refused. Swallowing that behind a generic
      // toast would make the failure impossible to act on.
      const fallback = t('Import failed.');
      if (!api.isError(error)) {
        setErrorMessage(fallback);
        return;
      }
      const apError = error.response?.data as ApErrorParams | undefined;
      const message =
        apError !== undefined &&
        typeof apError.params === 'object' &&
        apError.params !== null &&
        'message' in apError.params
          ? String(apError.params.message)
          : fallback;
      setErrorMessage(message);
    },
  });

  const canSubmit =
    !isPending &&
    (source === 'url'
      ? sourceUrl.trim().length > 0
      : rawSpec.trim().length > 0);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-2" />
          {t('Import a Swagger')}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('Import a Swagger')}</DialogTitle>
          <DialogDescription>
            {t(
              'Swagger 2.0 and OpenAPI 3.x are both accepted; a 2.0 document is converted on import.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <Tabs
            value={source}
            onValueChange={(value) => setSource(value as ImportSource)}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="url">{t('From a URL')}</TabsTrigger>
              <TabsTrigger value="paste">{t('Paste a document')}</TabsTrigger>
            </TabsList>
            <TabsContent value="url" className="pt-3">
              <Label htmlFor="swagger-url">{t('Swagger URL')}</Label>
              <Input
                id="swagger-url"
                placeholder="https://api.example.com/swagger.json"
                value={sourceUrl}
                onChange={(event) => setSourceUrl(event.target.value)}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t(
                  'The document is fetched again whenever you refresh this Swagger.',
                )}
              </p>
            </TabsContent>
            <TabsContent value="paste" className="pt-3">
              <Label htmlFor="swagger-spec">{t('JSON or YAML document')}</Label>
              <Textarea
                id="swagger-spec"
                className="font-mono text-xs"
                minRows={8}
                maxRows={16}
                value={rawSpec}
                onChange={(event) => setRawSpec(event.target.value)}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t('A pasted document cannot be refreshed automatically.')}
              </p>
            </TabsContent>
          </Tabs>

          <SwaggerAudienceField
            audience={audience}
            onAudienceChange={setAudience}
            tenantIds={tenantIds}
            onTenantIdsChange={setTenantIds}
          />

          <div>
            <Label htmlFor="swagger-display-name">{t('Name')}</Label>
            <Input
              id="swagger-display-name"
              placeholder={t('Taken from the document title if left empty')}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="swagger-base-url">{t('Base URL')}</Label>
            <Input
              id="swagger-base-url"
              placeholder="https://api.example.com"
              value={baseUrl}
              onChange={(event) => setBaseUrl(event.target.value)}
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              {t(
                "Taken from the document's servers entry if left empty. Set this when the document declares none.",
              )}
            </p>
          </div>

          {errorMessage !== null && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {errorMessage}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isPending}
          >
            {t('Cancel')}
          </Button>
          <Button disabled={!canSubmit} onClick={() => importSwagger()}>
            {isPending && <LoadingSpinner className="mr-2 h-4 w-4" />}
            {t('Import')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { ImportSwaggerDialog };
